import PropTypes from 'prop-types';
import { action, extendObservable, reaction } from 'mobx';
import { InputConfigurationStore } from '../../BrowserPolicies/DLPStore';
import ValueStore from '../../helpers/ValueStore';
import InputStore from '../../InputStore';
import { PolicyModalSectionProps } from '../PolicyModalStore';
import FlagStore from '../../FlagStore';

const DEFAULT_VALUES = {
    reputationCheck: true,
    threatScanning: true,
    allInput: true,
    clipboardPaste: true,
    pasteLimitEnabled: false,
    pasteLimit: -1,
    allowUploads: false,
    uploadsFileThreatScan: true,
    uploadsFileDlpScan: true,
    allowDownloads: false,
    downloadsFileDlpScan: true,
    downloadsFileThreatScan: true,
    allowFilePreview: false,
    allowExitIso: false,
};

export const EXIT_OPTION = {
    WITH_MSG: 'with_message',
    WITHOUT_MSG: 'without_message',
    AUTO: 'auto',
    NONE: 'none',
};

export default class PageRestrictionsStore {
    static PropType = PropTypes.shape({
        reputationCheck: FlagStore.PropType.isRequired,
        threatScanning: FlagStore.PropType.isRequired,
        allInput: FlagStore.PropType.isRequired,
        clipboardPaste: FlagStore.PropType.isRequired,
        pasteLimitEnabled: FlagStore.PropType.isRequired,
        pasteLimit: InputStore.PropType.isRequired,
        allowUploads: FlagStore.PropType.isRequired,
        uploadsFileThreatScan: FlagStore.PropType.isRequired,
        uploadsFileDlpScan: FlagStore.PropType.isRequired,
        allowDownloads: FlagStore.PropType.isRequired,
        downloadsFileDlpScan: FlagStore.PropType.isRequired,
        downloadsFileThreatScan: FlagStore.PropType.isRequired,
        allowFilePreview: FlagStore.PropType.isRequired,
        allowExitIso: FlagStore.PropType.isRequired,
        exitAutomatically: FlagStore.PropTypes,
        displayExitWarning: FlagStore.PropType,
        exitOption: ValueStore.PropTypes,
        // The action is non-reactive and changes in the ActionStore. The ContentRuleStore contains a reaction
        // keeping them in sync
        action: ValueStore.PropTypes,

        ...PolicyModalSectionProps,
    });

    static exitOptionToValues = (option) => ({
        [EXIT_OPTION.AUTO]: {
            exitAutomatically: true,
            displayExitWarning: false,
            allowExitIso: true
        },
        [EXIT_OPTION.WITHOUT_MSG]: {
            exitAutomatically: false,
            displayExitWarning: false,
            allowExitIso: true
        },
        [EXIT_OPTION.WITH_MSG]: {
            exitAutomatically: false,
            displayExitWarning: true,
            allowExitIso: true
        },
        [EXIT_OPTION.NONE]: {
            exitAutomatically: false,
            displayExitWarning: false,
            allowExitIso: false
        }
    })[option]

    static valuesToExitOption = ({ exitAutomatically, displayExitWarning, allowExitIso }) => {
        if (exitAutomatically) {
            return EXIT_OPTION.AUTO;
        }
        if (displayExitWarning) {
            return EXIT_OPTION.WITH_MSG;
        }
        if (allowExitIso) {
            return EXIT_OPTION.WITHOUT_MSG;
        }
        return EXIT_OPTION.NONE;
    }

    constructor(data = {}) {
        extendObservable(this, /** @class PageRestrictionsStore */{
            reputationCheck: new FlagStore(),
            threatScanning: new FlagStore(),
            allInput: new FlagStore(),
            clipboardPaste: new FlagStore(),
            pasteLimitEnabled: new FlagStore(),
            pasteLimit: new InputStore({ value: 0 }),
            allowUploads: new FlagStore(),
            uploadsFileThreatScan: new FlagStore(),
            uploadsFileDlpScan: new FlagStore(),
            allowDownloads: new FlagStore(),
            downloadsFileDlpScan: new FlagStore(),
            downloadsFileThreatScan: new FlagStore(),
            allowFilePreview: new FlagStore(),
            allowExitIso: new FlagStore(),
            exitOption: new ValueStore(),
            exitAutomatically: new FlagStore(),
            displayExitWarning: new FlagStore(),
            action: new ValueStore(),
            originalValue: {},
        }, data);

        reaction(() => this.exitOption.getValue(), (v) => {
            const values = PageRestrictionsStore.exitOptionToValues(v);

            this.exitAutomatically.setValue(values.exitAutomatically);
            this.displayExitWarning.setValue(values.displayExitWarning);
            this.allowExitIso.setValue(values.allowExitIso);
        });

        reaction(() => this.allInput.value, (isEnabled) => {
            if (!isEnabled) {
                this.clipboardPaste.setFalse();
            }
        });

        reaction(() => this.clipboardPaste.value, (isEnabled) => {
            if (!isEnabled) {
                this.pasteLimitEnabled.setFalse();
            }
        });

        this.pasteLimit.setValidator(InputConfigurationStore.pasteLimitValidator(this.pasteLimit));
    }

    isValid() {
        const vals = this.getValue();
        return this.action.getValue() === 'BLOCK'
            || !vals.allInput
            || !vals.clipboardPaste
            || !vals.pasteLimitEnabled
            || vals.pasteLimit > -1;
    }

    setValue = action((value) => {
        value = {
            ...DEFAULT_VALUES,
            ...(value || {}),
        };

        value.pasteLimitEnabled = value.pasteLimit !== -1;
        value.pasteLimit = value.pasteLimitEnabled ? value.pasteLimit : 0;

        this.originalValue = value;
        this.action.setValue(value.action);
        this.allInput.setValue(value.allInput); // ok
        this.reputationCheck.setValue(value.reputationCheck); // ok
        this.threatScanning.setValue(value.threatScanning); // ok now
        this.clipboardPaste.setValue(value.clipboardPaste); // ok
        this.allowUploads.setValue(value.allowUploads); // ok
        this.uploadsFileThreatScan.setValue(value.uploadsFileThreatScan); // ok now
        this.uploadsFileDlpScan.setValue(value.uploadsFileDlpScan); // ok now
        this.allowDownloads.setValue(value.allowDownloads); // ok
        this.downloadsFileDlpScan.setValue(value.downloadsFileDlpScan); // ok now
        this.downloadsFileThreatScan.setValue(value.downloadsFileThreatScan); // ok now
        this.allowFilePreview.setValue(value.allowFilePreview); // ok now
        this.allowExitIso.setValue(value.allowExitIso); // ok now
        this.pasteLimitEnabled.setValue(value.pasteLimitEnabled); // Frontend only
        this.pasteLimit.setValue(value.pasteLimit); // ok now?
        this.exitAutomatically.setValue(value.exitAutomatically);
        this.displayExitWarning.setValue(value.displayExitWarning);
        this.exitOption.setValue(PageRestrictionsStore.valuesToExitOption(this.getValue()));
    });

    getValue = () => ({
        reputationCheck: this.reputationCheck.value,
        threatScanning: this.threatScanning.value,
        allInput: this.allInput.value,
        clipboardPaste: this.clipboardPaste.value,
        pasteLimitEnabled: this.pasteLimitEnabled.value,
        allowUploads: this.allowUploads.value,
        uploadsFileThreatScan: this.uploadsFileThreatScan.value,
        uploadsFileDlpScan: this.uploadsFileDlpScan.value,
        allowDownloads: this.allowDownloads.value,
        downloadsFileDlpScan: this.downloadsFileDlpScan.value,
        downloadsFileThreatScan: this.downloadsFileThreatScan.value,
        allowFilePreview: this.allowFilePreview.value,
        allowExitIso: this.allowExitIso.value,
        exitAutomatically: this.exitAutomatically.getValue(),
        displayExitWarning: this.displayExitWarning.getValue(),
        pasteLimit: this.pasteLimitEnabled.isTrue ? parseInt(this.pasteLimit.value, 10) : -1
    });

    hasChanged = () =>
        !this.reputationCheck.equals(this.originalValue.reputationCheck) ||
        !this.threatScanning.equals(this.originalValue.threatScanning) ||
        !this.allInput.equals(this.originalValue.allInput) ||
        !this.clipboardPaste.equals(this.originalValue.clipboardPaste) ||
        !this.pasteLimitEnabled.equals(this.originalValue.pasteLimitEnabled) ||
        (this.pasteLimitEnabled.isTrue && !this.pasteLimit.equals(this.originalValue.pasteLimit)) ||
        !this.allowUploads.equals(this.originalValue.allowUploads) ||
        !this.uploadsFileThreatScan.equals(this.originalValue.uploadsFileThreatScan) ||
        !this.uploadsFileDlpScan.equals(this.originalValue.uploadsFileDlpScan) ||
        !this.allowDownloads.equals(this.originalValue.allowDownloads) ||
        !this.downloadsFileDlpScan.equals(this.originalValue.downloadsFileDlpScan) ||
        !this.downloadsFileThreatScan.equals(this.originalValue.downloadsFileThreatScan) ||
        !this.allowFilePreview.equals(this.originalValue.allowFilePreview) ||
        PageRestrictionsStore.valuesToExitOption(this.originalValue) !==
            PageRestrictionsStore.valuesToExitOption(this.getValue())
}
